import type { CSSProperties } from 'vue'

import VideoElement from './VideoElement.vue'

import type { FlashcardSize, VideoElementInput } from '../types'
import type { ElementDefinition } from '../runtime/elementDefinition'
import { scaleWidth } from '../runtime/math'

const estimateVideoBox = (input: VideoElementInput, size: FlashcardSize) => {
  const aspectRatio = typeof input.aspectRatio === 'number' ? input.aspectRatio : 16 / 9
  const baseSize = typeof input.size === 'number' ? input.size : 140
  if (!aspectRatio || aspectRatio <= 0 || !baseSize || baseSize <= 0) return null

  const widthPx = scaleWidth(baseSize, size)
  const heightPx = widthPx / aspectRatio
  if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || widthPx <= 0 || heightPx <= 0) {
    return null
  }
  return { widthPx, heightPx }
}

const definition: ElementDefinition<VideoElementInput> = {
  type: 'video',
  component: VideoElement,
  estimateBox: (input, size) => estimateVideoBox(input, size),
  toProps: (input, ctx) => {
    if (!ctx.baseStyle || typeof input.src !== 'string') {
      return null
    }

    const aspectRatio = typeof input.aspectRatio === 'number' ? input.aspectRatio : 16 / 9
    const baseSize = typeof input.size === 'number' ? input.size : 140
    if (!aspectRatio || aspectRatio <= 0 || !baseSize || baseSize <= 0) return null

    const widthPx = scaleWidth(baseSize, ctx.size)
    const heightPx = widthPx / aspectRatio
    if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || widthPx <= 0 || heightPx <= 0) {
      return null
    }

    const style: CSSProperties = {
      ...ctx.baseStyle,
      width: `${widthPx}px`,
      height: `${heightPx}px`,
      display: 'block',
      objectFit: 'cover',
      pointerEvents: 'auto',
      background: 'transparent',
    }

    const iconSizePx = scaleWidth(
      typeof input.iconSize === 'number' ? input.iconSize : 26,
      ctx.size,
    )
    const safeIconSizePx = Number.isFinite(iconSizePx) && iconSizePx > 0 ? iconSizePx : 26

    return {
      src: input.src,
      poster: typeof input.poster === 'string' ? input.poster : undefined,
      autoplay: Boolean(input.autoplay),
      loop: Boolean(input.loop),
      muted: Boolean(input.muted),
      playsInline: input.playsInline !== false,
      preload: input.preload ?? 'metadata',
      style,
      iconSizePx: safeIconSizePx,
    }
  },
}

export default definition
